import React from 'react';
import PropTypes from 'prop-types';

import Back from 'shared-vectors/icons/ModalBack';
import Close from 'shared-vectors/icons/MapToolsClose';
import Draw from 'shared-vectors/icons/MapDraw';
import { Icon } from 'shared-vectors';

import computeSearchWhere from 'shared-utils/src/computeSearchWhere';

import Grid from 'shared-components/components/Grid';
import GridItem from 'shared-components/components/GridItem';

import QbBtn from '../../../layout/QbBtn';

import { DRAWING_ACTIONS_COPY, DRAWING_ACTIONS_MAP, animated } from '../../../../constants';

import decorateMapLocality from '../../../../helpers/decorateMapLocality';

const Controls = ({
  searchMode,
  setSearchMode,
  device,
  hasDrawing,
  availableResults,
  availablePoi,
  updateFiltersToAddToMapQuery,
  deleteDraft,
  deletePolygon,
  revertPolygon,
  drawingData,
  polygonPath,
  confirmPolygon,
  openQbFiltersPanel,
  qbGoToSRP,
  qbSSearchAction,
  controlsVariant,
  isDrawing,
  startDrawingPolygon,
  exitQBDrawing,
  polygonIsNotValid,
}) => {

  const close = () => {
    if (drawingData && drawingData.action === DRAWING_ACTIONS_MAP.polygon) {
      revertPolygon();
    } else if (hasDrawing) {
      deletePolygon();
      setSearchMode(null);
    } else {
      deleteDraft();
      setSearchMode(null);
    }
  };

  const shapeFilter = polygonPath ? {
    geopolygon: [{ polygon: polygonPath }],
    q: null,
    geobounds: null,
    geocircle: null,
    nearby: null,
  } : {};

  const startSearch = () => {
    if (polygonIsNotValid) {
      deletePolygon();
      return;
    }
    if (controlsVariant === 'only-confirm-ssearch') {
      qbSSearchAction({
        mapLocations: decorateMapLocality({
          drawingData: {
            polygon: true,
          },
        }),
        mapFilters: shapeFilter,
        mapDrawingData: {
          action: DRAWING_ACTIONS_MAP.polygon,
          polygon: polygonPath,
          searchAddress: null,
          fromSSearch: true,
        },
      });
    } else if (qbGoToSRP) {
      qbGoToSRP({
        tempLocations: computeSearchWhere(
          decorateMapLocality({
            drawingData: {
              polygon: true,
            },
          }), []),
        shapeFilter,
        forceMap: true,
      });
    } else {
      confirmPolygon({ path: polygonPath, ar: availableResults, clickFrom: 'QB_MainButton' });
    }
  };

  let searchButtonCont = 'Nessun risultato';
  if (availableResults?.loading) {
    searchButtonCont = animated();
  } else if (availableResults?.value) {
    searchButtonCont = controlsVariant
      ? <>Conferma (<span className="tp-w--l">{availableResults.value}</span> risultati)</>
      : `Vedi ${availableResults.value} risultati`;
  }

  const actAsSmartphone = document.documentElement.clientWidth < 1024;

  const canSearch = !availableResults?.loading;

  const explanation = () => {
    if (!isDrawing) {
      return <>Sposta la mappa dove vuoi cercare.<br />Poi inizia a disegnare.</>;
    }
    return device === 'desktop' ? <>Fai click sulla mappa per definire i vertici.<br />Clicca sul primo punto per chiudere l’area.</> : 'Usa il dito per definire l’area.';
  };

  const startDrawingPolygonOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      startDrawingPolygon();
    }
  };

  const exitQBDrawingOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      exitQBDrawing();
    }
  };

  const startSearchOnEnter = (e) => {
    if (e.key === 'Enter' && canSearch) {
      e.stopPropagation();
      startSearch();
    }
  };

  const closeOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      close();
    }
  };

  return (
    <>      
      <div className="csa_mapsearch__bod__over__layer__top draw">
        <div className="csa_mapsearch__bod__over__layer__top__hdr c-bg--w mb--s">
          <div className="csa_mapsearch__bod__over__layer__top__hdr__close c-txt--secondary is-clickable" onClick={close}>
            <Icon name={actAsSmartphone ? Back : Close} width={actAsSmartphone ? null : '16px'} fill />
          </div>
          <img
            className="csa_mapsearch__bod__over__layer__top__hdr__icon"
            src="https://www.casa.it/external-resources/map/draw.svg"
            alt={DRAWING_ACTIONS_COPY.draw.desktop}
            width="100"
            height="80"
          />
          <div className="csa_mapsearch__bod__over__layer__top__hdr__claim">
            <p className="csa_mapsearch__bod__over__layer__top__hdr__claim--fst tp-s--l tp-w--l c-txt--f0">{DRAWING_ACTIONS_COPY.draw.mobile}</p>
            <p className={`csa_mapsearch__bod__over__layer__top__hdr__claim--sec tp-s--m ${searchMode} tp-w--s c-txt--f0`}>{DRAWING_ACTIONS_COPY.draw.secondary}</p>
          </div>
        </div>
        {(!hasDrawing && !actAsSmartphone) ? (
          <div className="csa_mapsearch__bod__over__layer__top__drawguide tp-s--m tp-w--s tp-a--c c-txt--f0">
            {explanation()}
          </div>
        ) : null}
        {(qbGoToSRP && !actAsSmartphone && !isDrawing && !hasDrawing) ? (
          <Grid className="" justify="center">
            <div
              role="button"
              tabIndex="0"
              className="qbmap__polybtn__btn mt--ml pr--m pl--ms tp-s--l tp-w--l b--s c-txt--w c-bg--primary c-br--primary is-clickable"
              onClick={startDrawingPolygon}
              onKeyDown={startDrawingPolygonOnEnter}
            >
              <Icon aria-hidden="true" name={Draw} className="mr--xs" width="24px" fill />Inizia a disegnare
            </div>
          </Grid>
        ) : null}
        {(qbGoToSRP && !actAsSmartphone && isDrawing && !hasDrawing) ? (
          <Grid className="" justify="center">
            <div
              role="button"
              tabIndex="0"
              className="qbmap__polybtn__btn mt--ml tp-s--l tp-w--m b--s tp-a--c c-txt--f0 c-bg--w c-br--border is-clickable"
              onClick={exitQBDrawing}
              onKeyDown={exitQBDrawingOnEnter}
            >
              Annulla
            </div>
          </Grid>
        ) : null}
      </div>        
      <div className="csa_mapsearch__bod__over__layer__bot">
        {hasDrawing ? (
          <div className="csa_mapsearch__draw__radiusSlider pr--m pb--ms pl--m c-bg--w">
            <Grid className="csa_mapsearch__bod__over__layer__bot__btns" align="center">
              {!controlsVariant ? (
                <QbBtn
                  availableResults={availableResults}
                  availablePoi={availablePoi}
                  shapeFilter={shapeFilter}
                  updateFiltersToAddToMapQuery={updateFiltersToAddToMapQuery}
                  openQbFiltersPanel={openQbFiltersPanel}
                  qbLocation={decorateMapLocality({
                    drawingData: {
                      polygon: true,
                    },
                  })}
                />
              ) : null}
              <GridItem className="csa_mapsearch__bod__over__layer__bot__btn">
                <div
                  role="button"
                  tabIndex="0"                
                  className={`csa_mapsearch__bod__over__layer__bot__btn tp-s--l tp-w--l c-txt--w c-bg--primary b--s c-br--primary tp-a--c is-clickable${!canSearch ? ' disabled' : ''}`}
                  onClick={startSearch}
                  onKeyDown={startSearchOnEnter}
                >
                  {searchButtonCont}
                </div>
              </GridItem>
            </Grid>
          </div>
        ) : null}
      </div>
      {!actAsSmartphone ? (
        <>
          <div
            className="csa_mapsearch__bod__over__layer__dskhandle is-clickable"
            onClick={close}
          />
          <Grid className="csa_mapsearch__bod__over__layer__reset" justify="center">
            <GridItem
              behavior="fixed"
              role="button"
              tabIndex="0"              
              className="csa_mapsearch__bod__over__layer__reset__btn pr--m pl--m tp-s--l tp-w--m b--s tp-a--c c-txt--secondary is-clickable"
              onClick={close}
              onKeyDown={closeOnEnter}
            >
              Annulla
            </GridItem>
          </Grid>
        </>
      ) : null}
    </>
  );
};

export default Controls;

Controls.propTypes = {
  searchMode: PropTypes.string,
  setSearchMode: PropTypes.func,
  hasDrawing: PropTypes.bool,
  device: PropTypes.string,
  updateFiltersToAddToMapQuery: PropTypes.func,
  deleteDraft: PropTypes.func,
  deletePolygon: PropTypes.func,
  revertPolygon: PropTypes.func,
  drawingData: PropTypes.instanceOf(Object),
  polygonPath: PropTypes.instanceOf(Array),
  confirmPolygon: PropTypes.func,
  availableResults: PropTypes.instanceOf(Object),
  viewportWidth: PropTypes.number,
  openQbFiltersPanel: PropTypes.func,
  qbGoToSRP: PropTypes.func,
  qbSSearchAction: PropTypes.func,
  controlsVariant: PropTypes.string,
  isDrawing: PropTypes.bool,
  startDrawingPolygon: PropTypes.func,
  exitQBDrawing: PropTypes.func,
  polygonIsNotValid: PropTypes.bool,
};

Controls.defaultProps = {
  searchMode: null,
  setSearchMode: null,
  hasDrawing: false,
  device: '',
  updateFiltersToAddToMapQuery: () => {},
  deleteDraft: () => {},
  deletePolygon: () => {},
  revertPolygon: () => {},
  drawingData: null,
  polygonPath: [],
  confirmPolygon: () => {},
  availableResults: null,
  viewportWidth: 0,
  openQbFiltersPanel: null,
  qbGoToSRP: null,
  qbSSearchAction: null,
  controlsVariant: null,
  isDrawing: false,
  startDrawingPolygon: () => {},
  exitQBDrawing: () => {},
  polygonIsNotValid: false,
};
